import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { SidebarContext } from '../../../context/SidebarContext';

/**
 * Bar chart of active users across the reporting windows the API gives us
 * (today / this month / this year). Apex handles the grow-in animation.
 */
const ActivityBarChart = ({
  title,
  subtitle,
  categories,
  data,
  accent,
  loading,
  seriesName,
  valueSuffix,
  emptyLabel
}) => {
  const { isDarkModeEnabled } = useContext(SidebarContext);

  const axisColor = isDarkModeEnabled ? '#9AA2B6' : '#878793';
  const gridColor = isDarkModeEnabled ? '#3A3952' : '#E7E8EA';
  const hasData = Array.isArray(data) && data.some((value) => Number(value) > 0);

  const options = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: true, easing: 'easeinout', speed: 800 },
      fontFamily: 'inherit'
    },
    plotOptions: {
      bar: { borderRadius: 6, columnWidth: '45%', borderRadiusApplication: 'end' }
    },
    colors: [accent],
    dataLabels: { enabled: false },
    grid: { borderColor: gridColor, strokeDashArray: 4, padding: { left: 8, right: 8 } },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: axisColor, fontSize: '12px' } }
    },
    yaxis: {
      labels: {
        style: { colors: axisColor, fontSize: '12px' },
        formatter: (value) => `${Math.round(value)}${valueSuffix}`
      }
    },
    tooltip: {
      theme: isDarkModeEnabled ? 'dark' : 'light',
      y: { formatter: (value) => `${value}${valueSuffix}` }
    }
  };

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder rounded-2xl p-4 md:p-5 h-full">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <h4 className="text-base font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
            {title}
          </h4>
          {subtitle ? <p className="text-xs text-shoorah-gray3">{subtitle}</p> : null}
        </div>
      </div>

      {hasData && !loading ? (
        <Chart
          options={options}
          series={[{ name: seriesName, data }]}
          type="bar"
          height={260}
          width="100%"
        />
      ) : (
        <div className="h-[260px] flex items-center justify-center">
          <span className="text-xs text-shoorah-gray3">
            {loading ? 'Loading…' : emptyLabel}
          </span>
        </div>
      )}
    </div>
  );
};

ActivityBarChart.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.arrayOf(PropTypes.number),
  accent: PropTypes.string,
  loading: PropTypes.bool,
  seriesName: PropTypes.string,
  valueSuffix: PropTypes.string,
  emptyLabel: PropTypes.string
};

ActivityBarChart.defaultProps = {
  accent: '#1F1F1F',
  seriesName: 'Active users',
  valueSuffix: '',
  emptyLabel: 'No data yet'
};

export default ActivityBarChart;
